import React, { useCallback, useRef, useState } from 'react';
import api from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { cn } from '../../lib/utils';
import { Badge, Button, Select, useConfirm } from '../../components/ui';
import {
  EyeIcon,
  SpinnerIcon,
  TrashIcon,
  UploadCloudIcon,
} from '../../components/ui/Icons';
import { FileAttachmentsFormBlock } from '../../components/attachments/FileAttachmentsFormBlock';
import { FileIcon } from '../../components/attachments/FileIcon';
import { formatFileSize } from '../../components/attachments/fileIconUtils';
import {
  ACC_DOCUMENT_ATTACHMENT_ACCEPT,
  ACC_DOCUMENT_ATTACHMENT_HINT,
} from '../../components/attachments/attachmentAccept';
import { formatDate as formatDateGlobal } from '../../config/appConfig';
import {
  MGA_ATTACHMENT_KIND_OPTIONS,
  mgaAttachmentKindLabel,
  type MgaAttachmentKind,
} from './attachmentKinds';
import type { MgaPeriodAttachment } from './useData';

type Props = {
  periodId: number;
  files: MgaPeriodAttachment[];
  canEdit: boolean;
  /** Closed periods can still upload (e.g. signed PDF) but not delete. */
  canDelete: boolean;
  onChanged: () => void | Promise<unknown>;
  /** `workspace` = full-bleed sheet tab; `panel` = compact dashboard block. */
  layout?: 'workspace' | 'panel';
  /** Closed-period hint for workspace header. */
  periodClosed?: boolean;
};

export function PeriodFilesPanel({
  periodId,
  files,
  canEdit,
  canDelete,
  onChanged,
  layout = 'panel',
  periodClosed = false,
}: Props) {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { showError, showSuccess } = useNotification();
  const confirm = useConfirm();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [viewingId, setViewingId] = useState<number | null>(null);
  const [uploadKind, setUploadKind] = useState<MgaAttachmentKind>('OTHER');
  const [kindFilter, setKindFilter] = useState<'ALL' | MgaAttachmentKind>('ALL');

  const uploadFiles = useCallback(
    async (list: File[], kind: MgaAttachmentKind = uploadKind) => {
      if (!canEdit || list.length === 0) return;
      setBusy(true);
      try {
        const formData = new FormData();
        formData.append('kind', kind);
        for (const f of list) formData.append('files', f);
        await api.post(`/mga/periods/${periodId}/files`, formData);
        showSuccess(
          list.length === 1
            ? `${mgaAttachmentKindLabel(kind)} uploaded.`
            : `${list.length} files uploaded (${mgaAttachmentKindLabel(kind)}).`,
        );
        await onChanged();
      } catch (e: unknown) {
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? String(
                (e as { response?: { data?: { message?: string | string[] } } }).response?.data
                  ?.message ?? '',
              )
            : '';
        showError(Array.isArray(msg) ? msg.join(' ') : msg || 'Could not upload file(s).');
      } finally {
        setBusy(false);
      }
    },
    [canEdit, onChanged, periodId, showError, showSuccess, uploadKind],
  );

  const updateKind = useCallback(
    async (fileId: number, kind: MgaAttachmentKind) => {
      if (!canDelete) return;
      setBusy(true);
      try {
        await api.patch(`/mga/periods/${periodId}/files/${fileId}`, { kind });
        showSuccess(`Labeled as ${mgaAttachmentKindLabel(kind)}.`);
        await onChanged();
      } catch (e: unknown) {
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? String(
                (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? '',
              )
            : '';
        showError(msg || 'Could not update label.');
      } finally {
        setBusy(false);
      }
    },
    [canDelete, onChanged, periodId, showError, showSuccess],
  );

  const handleRemove = useCallback(
    async (fileId: number, fileName?: string) => {
      if (!canDelete) {
        showError('Files cannot be deleted on a closed period.');
        return;
      }
      const ok = await confirm({
        title: 'Remove file?',
        message: fileName
          ? `Delete “${fileName}” from this MGA period? This cannot be undone.`
          : 'Delete this file from the MGA period? This cannot be undone.',
        confirmText: 'Remove',
        cancelText: 'Cancel',
        confirmVariant: 'danger',
      });
      if (!ok) return;
      setBusy(true);
      try {
        await api.delete(`/mga/periods/${periodId}/files/${fileId}`);
        showSuccess('File removed.');
        await onChanged();
      } catch (e: unknown) {
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? String(
                (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? '',
              )
            : '';
        showError(msg || 'Could not remove file.');
      } finally {
        setBusy(false);
      }
    },
    [canDelete, confirm, onChanged, periodId, showError, showSuccess],
  );

  const handleView = useCallback(
    async (fileId: number, fileName: string) => {
      setViewingId(fileId);
      try {
        const response = await api.get(`/mga/periods/${periodId}/files/${fileId}`, {
          responseType: 'blob',
        });
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'application/octet-stream',
        });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      } catch {
        showError(`Could not open ${fileName}.`);
      } finally {
        setViewingId(null);
      }
    },
    [periodId, showError],
  );

  if (layout === 'panel') {
    const sorted = [...files].sort(
      (a, b) => new Date(b.uploadedAt || 0).getTime() - new Date(a.uploadedAt || 0).getTime(),
    );
    return (
      <div className={cn(busy && 'pointer-events-none opacity-70')}>
        {sorted.length === 0 ? (
          <p className={cn('px-4 py-2.5 text-sm', colors.textMuted)}>
            No attachments
            {canEdit ? ' — drop files on the sheet Files tab to upload' : null}
          </p>
        ) : (
          <ul className={cn('divide-y', colors.border)}>
            {sorted.map((f) => {
              const viewing = viewingId === f.id;
              return (
                <li
                  key={f.id}
                  className={cn('flex items-center gap-2.5 px-4 py-2', colors.interactiveHover)}
                >
                  <FileIcon fileName={f.fileName} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className={cn('truncate text-sm font-medium', colors.text)} title={f.fileName}>
                      {f.fileName}
                    </p>
                    <p className={cn('truncate text-[11px]', colors.textMuted)}>
                      {[
                        mgaAttachmentKindLabel(f.kind || 'OTHER'),
                        f.fileSize != null ? formatFileSize(f.fileSize) : null,
                        f.uploadedAt ? formatDateGlobal(f.uploadedAt) : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      disabled={viewing}
                      title="View"
                      aria-label={`View ${f.fileName}`}
                      onClick={() => void handleView(f.id, f.fileName)}
                    >
                      {viewing ? (
                        <SpinnerIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </Button>
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={cn('h-8 w-8 p-0', colors.error)}
                        title="Remove"
                        aria-label={`Remove ${f.fileName}`}
                        onClick={() => void handleRemove(f.id, f.fileName)}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {canEdit ? (
          <div className={cn('border-t px-4 py-2', colors.border)}>
            <FileAttachmentsFormBlock
              newFiles={[]}
              existingFiles={[]}
              onNewFilesChange={(next) => {
                if (next.length) void uploadFiles(next);
              }}
              onRemoveExisting={() => undefined}
              label=""
              hint={ACC_DOCUMENT_ATTACHMENT_HINT}
              accept={ACC_DOCUMENT_ATTACHMENT_ACCEPT}
              readOnly={false}
              canRemoveExisting={false}
              variant="compact"
            />
          </div>
        ) : null}
      </div>
    );
  }

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (canEdit && e.dataTransfer.types.includes('Files')) setDragActive(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragActive(false);
  };
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (!canEdit) return;
    void uploadFiles(Array.from(e.dataTransfer.files || []), uploadKind);
  };

  const visibleFiles =
    kindFilter === 'ALL' ? files : files.filter((f) => (f.kind || 'OTHER') === kindFilter);

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col overflow-hidden rounded-xl border',
        colors.border,
        colors.surface,
        busy && 'pointer-events-none opacity-70',
        dragActive && canEdit && (isDark ? 'ring-2 ring-indigo-400/50' : 'ring-2 ring-indigo-400/60'),
      )}
      onDragEnter={canEdit ? onDragEnter : undefined}
      onDragOver={canEdit ? onDragOver : undefined}
      onDragLeave={canEdit ? onDragLeave : undefined}
      onDrop={canEdit ? onDrop : undefined}
    >
      <div
        className={cn(
          'flex shrink-0 flex-col gap-3 border-b px-4 py-3 sm:px-5',
          colors.border,
          colors.headerBg,
        )}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={cn('text-sm font-semibold', colors.text)}>Period files</h2>
              <span
                className={cn(
                  'rounded-md px-1.5 py-0.5 text-[11px] font-semibold tabular-nums',
                  colors.surfaceHover,
                  colors.textSecondary,
                )}
              >
                {files.length}
              </span>
            </div>
            <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
              {ACC_DOCUMENT_ATTACHMENT_HINT}
              {periodClosed ? ' · Closed: upload OK, delete locked' : null}
            </p>
          </div>
          {canEdit ? (
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-44">
                <Select
                  value={uploadKind}
                  options={MGA_ATTACHMENT_KIND_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                  showClearButton={false}
                  onChange={(e) => setUploadKind(e.target.value as MgaAttachmentKind)}
                />
              </div>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept={ACC_DOCUMENT_ATTACHMENT_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  void uploadFiles(Array.from(e.target.files || []), uploadKind);
                  e.target.value = '';
                }}
              />
              <Button
                type="button"
                size="sm"
                onClick={() => inputRef.current?.click()}
                className="shrink-0"
              >
                <UploadCloudIcon className="h-4 w-4" />
                Upload
              </Button>
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setKindFilter('ALL')}
            className={cn(
              'rounded-md px-2 py-1 text-[11px] font-medium transition-colors',
              kindFilter === 'ALL'
                ? isDark
                  ? 'bg-indigo-500/25 text-indigo-100'
                  : 'bg-indigo-50 text-indigo-900'
                : cn(colors.textMuted, colors.interactiveHover),
            )}
          >
            All ({files.length})
          </button>
          {MGA_ATTACHMENT_KIND_OPTIONS.map((o) => {
            const n = files.filter((f) => (f.kind || 'OTHER') === o.value).length;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => setKindFilter(o.value)}
                className={cn(
                  'rounded-md px-2 py-1 text-[11px] font-medium transition-colors',
                  kindFilter === o.value
                    ? isDark
                      ? 'bg-indigo-500/25 text-indigo-100'
                      : 'bg-indigo-50 text-indigo-900'
                    : cn(colors.textMuted, colors.interactiveHover),
                )}
              >
                {o.label} ({n})
              </button>
            );
          })}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-4 sm:p-5">
        {files.length === 0 ? (
          <button
            type="button"
            disabled={!canEdit}
            onClick={() => canEdit && inputRef.current?.click()}
            className={cn(
              'flex h-full min-h-[18rem] w-full flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors',
              colors.border,
              canEdit ? colors.interactiveHover : 'cursor-default',
              dragActive &&
                canEdit &&
                (isDark
                  ? 'border-indigo-400 bg-indigo-950/30'
                  : 'border-indigo-400 bg-indigo-50/70'),
            )}
          >
            <div
              className={cn(
                'mb-3 flex h-14 w-14 items-center justify-center rounded-2xl',
                isDark ? 'bg-indigo-500/15 text-indigo-300' : 'bg-indigo-50 text-indigo-700',
              )}
            >
              <UploadCloudIcon className="h-7 w-7" />
            </div>
            <p className={cn('text-base font-semibold', colors.text)}>
              {dragActive ? 'Drop files to upload' : canEdit ? 'Drop files here' : 'No files yet'}
            </p>
            <p className={cn('mt-1.5 max-w-md text-sm', colors.textMuted)}>
              {canEdit
                ? `Uploading as “${mgaAttachmentKindLabel(uploadKind)}”. Choose a label in the toolbar first.`
                : 'Supporting documents for this period will appear here.'}
            </p>
          </button>
        ) : visibleFiles.length === 0 ? (
          <p className={cn('py-16 text-center text-sm', colors.textMuted)}>
            No files in this label. Choose another filter or upload with that label.
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {visibleFiles.map((f) => {
              const viewing = viewingId === f.id;
              const kind = (f.kind || 'OTHER') as MgaAttachmentKind;
              return (
                <li
                  key={f.id}
                  className={cn(
                    'group flex flex-col rounded-xl border p-4 transition-colors',
                    colors.border,
                    colors.interactiveHover,
                  )}
                >
                  <div className="flex items-start gap-3">
                    <FileIcon fileName={f.fileName} size="md" />
                    <div className="min-w-0 flex-1">
                      <p className={cn('truncate text-sm font-semibold', colors.text)} title={f.fileName}>
                        {f.fileName}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <Badge variant="secondary">{mgaAttachmentKindLabel(kind)}</Badge>
                        <span className={cn('text-xs tabular-nums', colors.textMuted)}>
                          {[
                            f.fileSize != null ? formatFileSize(f.fileSize) : null,
                            f.uploadedAt ? formatDateGlobal(f.uploadedAt) : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </div>
                    </div>
                  </div>
                  {canDelete ? (
                    <div className="mt-3">
                      <Select
                        value={kind}
                        options={MGA_ATTACHMENT_KIND_OPTIONS.map((o) => ({
                          value: o.value,
                          label: o.label,
                        }))}
                        showClearButton={false}
                        onChange={(e) =>
                          void updateKind(f.id, e.target.value as MgaAttachmentKind)
                        }
                      />
                    </div>
                  ) : null}
                  <div className={cn('mt-3 flex items-center gap-1 border-t pt-3', colors.border)}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 flex-1"
                      disabled={viewing}
                      onClick={() => void handleView(f.id, f.fileName)}
                    >
                      {viewing ? (
                        <SpinnerIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                      View
                    </Button>
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={cn('h-8', colors.error)}
                        onClick={() => void handleRemove(f.id, f.fileName)}
                        title="Remove"
                        aria-label={`Remove ${f.fileName}`}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
            {canEdit ? (
              <li>
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className={cn(
                    'flex h-full min-h-[8.5rem] w-full flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors',
                    colors.border,
                    colors.interactiveHover,
                    dragActive &&
                      (isDark
                        ? 'border-indigo-400 bg-indigo-950/25'
                        : 'border-indigo-400 bg-indigo-50/60'),
                  )}
                >
                  <UploadCloudIcon className={cn('mb-2 h-6 w-6', colors.textMuted)} />
                  <span className={cn('text-sm font-medium', colors.text)}>
                    {dragActive ? 'Drop to add' : 'Add files'}
                  </span>
                  <span className={cn('mt-1 text-[11px]', colors.textMuted)}>
                    as {mgaAttachmentKindLabel(uploadKind)}
                  </span>
                </button>
              </li>
            ) : null}
          </ul>
        )}
      </div>
    </div>
  );
}

export default PeriodFilesPanel;
